import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { FFmpeg } from '@ffmpeg/ffmpeg';
import { toBlobURL } from '@ffmpeg/util';
import '../styles/ExportVote.css';
import '../styles/BaseForm.css';
import '../styles/canvas.css';
import '../styles/ExportImage.css';
import fr from '../locales/fr';
import en from '../locales/en';

const MAX_SIZE_MB = 30;
const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;
const MAX_DURATION_SECONDS = 30;
const ffmpeg = new FFmpeg();
let ffmpegLoaded = false;

function ExportVideo({ ws }) {
  const browserLang = navigator.language || navigator.userLanguage;
  const language = browserLang.startsWith('fr') ? 'fr' : 'en';
  const translations = language === 'fr' ? fr : en;

  const location = useLocation();
  const navigate = useNavigate();

  const allowed =
    location.state?.fromMediaSelect ||
    sessionStorage.getItem('canAccessExportVideo');

  const [videoUrl, setVideoUrl] = useState('');
  const [isGif, setIsGif] = useState(false);
  const [isCompressing, setIsCompressing] = useState(false);
  const [compressionProgress, setCompressionProgress] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);
  const [trimStart, setTrimStart] = useState(0);
  const [trimEnd, setTrimEnd] = useState(0);
  const [cropArea, setCropArea] = useState({ x: 0, y: 0, width: 1920, height: 1080 });
  const [videoResolution, setVideoResolution] = useState({ width: 1920, height: 1080 });
  const [showEditor, setShowEditor] = useState(false);
  const [originalFile, setOriginalFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [cropScale, setCropScale] = useState(1);
  const [isDraggingTimeline, setIsDraggingTimeline] = useState(false);
  const [timelineDragType, setTimelineDragType] = useState(null); // 'start', 'end', 'middle'
  const [displayScale, setDisplayScale] = useState(1);

  const videoRef = React.useRef(null);
  const containerRef = React.useRef(null);

  const DISPLAY_WIDTH = 1920;
  const DISPLAY_HEIGHT = 1080;

  // Nettoyage des URLs blob lors du démontage du composant
  React.useEffect(() => {
    return () => {
      if (videoUrl) {
        URL.revokeObjectURL(videoUrl);
      }
    };
  }, [videoUrl]);

  // Calculer le displayScale pour adapter l'affichage à l'écran
  const calculateDisplayScale = () => {
    if (!videoResolution.width || !videoResolution.height) return 1;

    // Taille maximale disponible pour l'affichage avec marges de sécurité
    const maxWidth = window.innerWidth - 80;
    const maxHeight = window.innerHeight - 350;

    // Dimensions virtuelles (vidéo)
    const virtualWidth = videoResolution.width;
    const virtualHeight = videoResolution.height;

    // Calculer le ratio pour que tout rentre à l'écran
    const scaleX = maxWidth / virtualWidth;
    const scaleY = maxHeight / virtualHeight;

    // Prendre le plus petit ratio et limiter entre 0.1 et 1
    return Math.max(0.1, Math.min(1, Math.min(scaleX, scaleY)));
  };

  // Recalculer displayScale quand la vidéo ou la fenêtre change
  React.useEffect(() => {
    const newDisplayScale = calculateDisplayScale();
    setDisplayScale(newDisplayScale);

    const handleResize = () => {
      setDisplayScale(calculateDisplayScale());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [videoResolution]);

  // Gestionnaires pour déplacer la zone de crop
  const handleMouseDown = (e) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const screenX = e.clientX - rect.left;
    const screenY = e.clientY - rect.top;

    // Convertir coordonnées écran → virtuelles
    const virtualX = screenX / displayScale;
    const virtualY = screenY / displayScale;

    // Toujours permettre le drag si on clique dans la zone de crop
    if (virtualX >= cropArea.x && virtualX <= cropArea.x + cropArea.width &&
        virtualY >= cropArea.y && virtualY <= cropArea.y + cropArea.height) {
      setIsDragging(true);
      setDragStart({ x: virtualX - cropArea.x, y: virtualY - cropArea.y });
      e.preventDefault();
    }
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const screenX = e.clientX - rect.left;
    const screenY = e.clientY - rect.top;

    // Convertir coordonnées écran → virtuelles
    const virtualX = screenX / displayScale;
    const virtualY = screenY / displayScale;

    let newX = virtualX - dragStart.x;
    let newY = virtualY - dragStart.y;

    newX = Math.max(0, Math.min(videoResolution.width - cropArea.width, newX));
    newY = Math.max(0, Math.min(videoResolution.height - cropArea.height, newY));

    setCropArea({ ...cropArea, x: newX, y: newY });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleTouchStart = (e) => {
    if (!containerRef.current || e.touches.length !== 1) return;
    const touch = e.touches[0];
    const rect = containerRef.current.getBoundingClientRect();
    const screenX = touch.clientX - rect.left;
    const screenY = touch.clientY - rect.top;

    // Convertir coordonnées écran → virtuelles
    const virtualX = screenX / displayScale;
    const virtualY = screenY / displayScale;

    // Toujours permettre le drag si on clique dans la zone de crop
    if (virtualX >= cropArea.x && virtualX <= cropArea.x + cropArea.width &&
        virtualY >= cropArea.y && virtualY <= cropArea.y + cropArea.height) {
      setIsDragging(true);
      setDragStart({ x: virtualX - cropArea.x, y: virtualY - cropArea.y });
      e.preventDefault();
    }
  };

  const handleTouchMove = (e) => {
    if (!isDragging || !containerRef.current || e.touches.length !== 1) return;
    e.preventDefault();
    const touch = e.touches[0];
    const rect = containerRef.current.getBoundingClientRect();
    const screenX = touch.clientX - rect.left;
    const screenY = touch.clientY - rect.top;

    // Convertir coordonnées écran → virtuelles
    const virtualX = screenX / displayScale;
    const virtualY = screenY / displayScale;

    let newX = virtualX - dragStart.x;
    let newY = virtualY - dragStart.y;

    newX = Math.max(0, Math.min(videoResolution.width - cropArea.width, newX));
    newY = Math.max(0, Math.min(videoResolution.height - cropArea.height, newY));

    setCropArea({ ...cropArea, x: newX, y: newY });
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  // Event listeners pour le drag
  React.useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, dragStart, cropArea, videoResolution]);

  // Appliquer le zoom (maintient le ratio 16:9)
  const applyCropScale = (newScale) => {
    newScale = Math.max(0.1, Math.min(3, newScale));
    setCropScale(newScale);

    const targetWidth = 1920;
    const targetHeight = 1080;
    const newWidth = targetWidth * newScale;
    const newHeight = targetHeight * newScale;

    // Limiter par la résolution de la vidéo
    const finalWidth = Math.min(newWidth, videoResolution.width);
    const finalHeight = Math.min(newHeight, videoResolution.height);

    // Ajuster la position pour rester dans les limites
    setCropArea(prev => ({
      x: Math.max(0, Math.min(videoResolution.width - finalWidth, prev.x)),
      y: Math.max(0, Math.min(videoResolution.height - finalHeight, prev.y)),
      width: finalWidth,
      height: finalHeight
    }));
  };

  // Gestionnaires pour la timeline
  const handleTimelineMouseDown = (e, type) => {
    setIsDraggingTimeline(true);
    setTimelineDragType(type);
    e.stopPropagation();
    e.preventDefault();
  };

  const handleTimelineTouchStart = (e, type) => {
    if (e.touches.length !== 1) return;
    setIsDraggingTimeline(true);
    setTimelineDragType(type);
    e.stopPropagation();
    e.preventDefault();
  };

  const updateTimelinePosition = (clientX, timelineRef) => {
    if (!isDraggingTimeline || !timelineRef.current) return;

    const rect = timelineRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percentage = Math.max(0, Math.min(1, x / rect.width));
    const time = percentage * videoDuration;

    if (timelineDragType === 'start') {
      const newStart = Math.max(0, Math.min(time, trimEnd - 0.1));
      setTrimStart(newStart);
      if (trimEnd - newStart > MAX_DURATION_SECONDS) {
        setTrimEnd(newStart + MAX_DURATION_SECONDS);
      }
      if (videoRef.current) {
        videoRef.current.currentTime = newStart;
      }
    } else if (timelineDragType === 'end') {
      const newEnd = Math.max(trimStart + 0.1, Math.min(time, videoDuration));
      setTrimEnd(newEnd);
      if (newEnd - trimStart > MAX_DURATION_SECONDS) {
        setTrimStart(newEnd - MAX_DURATION_SECONDS);
      }
    } else if (timelineDragType === 'middle') {
      const duration = trimEnd - trimStart;
      let newStart = time - duration / 2;
      newStart = Math.max(0, Math.min(newStart, videoDuration - duration));
      setTrimStart(newStart);
      setTrimEnd(newStart + duration);
      if (videoRef.current) {
        videoRef.current.currentTime = newStart;
      }
    }
  };

  const handleTimelineMouseMove = (e, timelineRef) => {
    updateTimelinePosition(e.clientX, timelineRef);
  };

  const handleTimelineTouchMove = (e, timelineRef) => {
    if (e.touches.length !== 1) return;
    e.preventDefault();
    updateTimelinePosition(e.touches[0].clientX, timelineRef);
  };

  const handleTimelineMouseUp = () => {
    setIsDraggingTimeline(false);
    setTimelineDragType(null);
  };

  const handleTimelineTouchEnd = () => {
    setIsDraggingTimeline(false);
    setTimelineDragType(null);
  };

  // Event listeners pour la timeline
  React.useEffect(() => {
    if (isDraggingTimeline) {
      const timelineEl = document.getElementById('video-timeline');

      const handleMove = (e) => {
        if (timelineEl) {
          handleTimelineMouseMove(e, { current: timelineEl });
        }
      };

      const handleTouchMoveGlobal = (e) => {
        if (timelineEl) {
          handleTimelineTouchMove(e, { current: timelineEl });
        }
      };

      window.addEventListener('mousemove', handleMove);
      window.addEventListener('mouseup', handleTimelineMouseUp);
      window.addEventListener('touchmove', handleTouchMoveGlobal, { passive: false });
      window.addEventListener('touchend', handleTimelineTouchEnd);

      return () => {
        window.removeEventListener('mousemove', handleMove);
        window.removeEventListener('mouseup', handleTimelineMouseUp);
        window.removeEventListener('touchmove', handleTouchMoveGlobal);
        window.removeEventListener('touchend', handleTimelineTouchEnd);
      };
    }
  }, [isDraggingTimeline, timelineDragType, trimStart, trimEnd, videoDuration]);

  useEffect(() => {
    if (allowed) {
      sessionStorage.removeItem('canAccessExportVideo');
    }
  }, [allowed]);

  if (!allowed) {
    return <Navigate to="/mediaSelect" replace />;
  }

  const sendToWebSocket = (data) => {
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      console.error('WebSocket non connecté');
      alert(translations.exportVideo.connectionLostError);
      return false;
    }
    ws.send(data);
    return true;
  };

  const fetchFile = async (file) => {
    return new Uint8Array(await file.arrayBuffer());
  };

  const getVideoResolution = (file) => {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      video.preload = 'metadata';

      video.onloadedmetadata = () => {
        const width = video.videoWidth;
        const height = video.videoHeight;
        URL.revokeObjectURL(video.src);
        resolve({ width, height });
      };

      video.onerror = () => {
        URL.revokeObjectURL(video.src);
        reject(new Error('Erreur lors de la lecture de la vidéo.'));
      };
      video.src = URL.createObjectURL(file);
    });
  };

  const getVideoDuration = (file) => {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      video.preload = 'metadata';

      video.onloadedmetadata = () => {
        URL.revokeObjectURL(video.src);
        resolve(video.duration);
      };

      video.onerror = () => {
        URL.revokeObjectURL(video.src);
        reject(new Error('Impossible de lire la durée de la vidéo.'));
      };
      video.src = URL.createObjectURL(file);
    });
  };

  const loadFFmpeg = async () => {
    if (ffmpegLoaded) return;

    try {
      const baseURL = 'https://unpkg.com/@ffmpeg/core@0.12.6/dist/umd';

      // Configure le listener de progression
      ffmpeg.on('progress', ({ progress }) => {
        const percentage = Math.round(progress * 100);
        setCompressionProgress(percentage);
      });

      await ffmpeg.load({
        coreURL: await toBlobURL(`${baseURL}/ffmpeg-core.js`, 'text/javascript'),
        wasmURL: await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, 'application/wasm'),
      });
      ffmpegLoaded = true;
    } catch (error) {
      console.error('Erreur lors du chargement de FFmpeg:', error);
      throw new Error('Impossible de charger FFmpeg');
    }
  };

  const convertGifToVideo = async (file) => {
    await loadFFmpeg();

    await ffmpeg.writeFile('input.gif', await fetchFile(file));

    await ffmpeg.exec([
      '-i', 'input.gif',
      '-movflags', 'faststart',
      '-pix_fmt', 'yuv420p',
      '-vf', 'scale=trunc(iw/2)*2:trunc(ih/2)*2',
      '-vcodec', 'libx264',
      '-preset', 'veryfast',
      '-b:v', '2M',
      '-maxrate', '2M',
      '-bufsize', '2M',
      'output.mp4'
    ]);

    const data = await ffmpeg.readFile('output.mp4');
    return new Blob([data], { type: 'video/mp4' });
  };

  const cropAndTrimVideo = async (file, startTime, endTime, crop) => {
    await loadFFmpeg();

    await ffmpeg.writeFile('input.mp4', await fetchFile(file));

    const duration = endTime - startTime;
    const cropFilter = `crop=${crop.width}:${crop.height}:${crop.x}:${crop.y},scale=1920:1080`;

    await ffmpeg.exec([
      '-ss', startTime.toString(),
      '-t', duration.toString(),
      '-i', 'input.mp4',
      '-vf', cropFilter,
      '-vcodec', 'libx264',
      '-preset', 'veryfast',
      '-b:v', '2M',
      '-maxrate', '2M',
      '-bufsize', '2M',
      '-movflags', '+faststart',
      'output.mp4'
    ]);

    const data = await ffmpeg.readFile('output.mp4');
    return new Blob([data], { type: 'video/mp4' });
  };

  const compressAndResizeVideo = async (file, forceResize = false) => {
    await loadFFmpeg();

    const { width, height } = await getVideoResolution(file);
    const shouldResize = forceResize || height > 1080;
    const scaleOption = shouldResize ? ['-vf', 'scale=1920:1080'] : [];

    await ffmpeg.writeFile('input.mp4', await fetchFile(file));

    await ffmpeg.exec([
      '-i', 'input.mp4',
      '-vcodec', 'libx264',
      '-preset', 'veryfast',
      '-b:v', '2M',
      '-maxrate', '2M',
      '-bufsize', '2M',
      ...scaleOption,
      'output.mp4'
    ]);

    const data = await ffmpeg.readFile('output.mp4');
    return new Blob([data], { type: 'video/mp4' });
  };

  const handleVideoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setOriginalFile(file);
    const isGifFile = file.type === 'image/gif';

    const unityID = localStorage.getItem('UNITY');
    const pseudo = localStorage.getItem('pseudo');
    const pseudoColor = localStorage.getItem('pseudoColor');

    // Gestion des GIFs - conversion en MP4 pour compatibilité
    if (isGifFile) {
      setIsCompressing(true);
      setCompressionProgress(0);

      try {
        const convertedVideo = await convertGifToVideo(file);

        if (convertedVideo.size > MAX_SIZE_BYTES) {
          alert(`${translations.exportVideo.gifTooLargeError} ${MAX_SIZE_MB} Mo.`);
          setIsCompressing(false);
          return;
        }

        const videoUrl = URL.createObjectURL(convertedVideo);
        setVideoUrl(videoUrl);
        setIsGif(false); // Traiter comme une vidéo après conversion

        const reader = new FileReader();
        reader.onload = () => {
          const dataURL = reader.result;
          if (sendToWebSocket(`${unityID}:NPþ${pseudo}þ${pseudoColor}þVIDþ${dataURL}`)) {
            sessionStorage.setItem('canAccessReady', 'true');
            navigate('/Ready', { state: { fromAuthorized: true } });
          }
        };
        reader.readAsDataURL(convertedVideo);
      } catch (err) {
        alert(translations.exportVideo.gifConversionError);
        console.error(err);
      } finally {
        setIsCompressing(false);
      }
      return;
    }

    // Vérifie la durée pour les vidéos
    let duration;
    try {
      duration = await getVideoDuration(file);
      setVideoDuration(duration);
      setTrimEnd(Math.min(duration, MAX_DURATION_SECONDS));
    } catch (err) {
      alert(translations.exportVideo.durationCheckError);
      return;
    }

    // Obtenir la résolution
    let resolution;
    try {
      resolution = await getVideoResolution(file);
      setVideoResolution(resolution);

      // Initialiser la zone de crop au centre pour un ratio 16:9 (1920x1080)
      // Calculer le scale initial pour que la zone de crop fasse 1920x1080 en ratio
      const targetWidth = 1920;
      const targetHeight = 1080;
      const scaleFromWidth = resolution.width / targetWidth;
      const scaleFromHeight = resolution.height / targetHeight;
      const initialScale = Math.min(scaleFromWidth, scaleFromHeight);

      setCropScale(initialScale);

      const cropWidth = targetWidth * initialScale;
      const cropHeight = targetHeight * initialScale;
      const cropX = Math.max(0, (resolution.width - cropWidth) / 2);
      const cropY = Math.max(0, (resolution.height - cropHeight) / 2);

      setCropArea({ x: cropX, y: cropY, width: cropWidth, height: cropHeight });
    } catch (err) {
      alert(translations.exportVideo.metadataReadError);
      console.error(err);
      return;
    }

    // Afficher l'éditeur au lieu de traiter directement
    const videoObjectURL = URL.createObjectURL(file);
    setVideoUrl(videoObjectURL);
    setShowEditor(true);
  };

  const handleConfirmEdit = async () => {
    if (!originalFile) return;

    const unityID = localStorage.getItem('UNITY');
    const pseudo = localStorage.getItem('pseudo');
    const pseudoColor = localStorage.getItem('pseudoColor');

    setIsCompressing(true);
    setCompressionProgress(0);

    try {
      // Appliquer le crop et le trim
      const editedVideo = await cropAndTrimVideo(
        originalFile,
        trimStart,
        trimEnd,
        cropArea
      );

      if (editedVideo.size > MAX_SIZE_BYTES) {
        alert(`${translations.exportVideo.videoTooLargeError} ${MAX_SIZE_MB} Mo. ${translations.exportVideo.reduceMessage}`);
        setIsCompressing(false);
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        const dataURL = reader.result;
        if (sendToWebSocket(`${unityID}:NPþ${pseudo}þ${pseudoColor}þVIDþ${dataURL}`)) {
          navigate('/Ready');
        }
      };
      reader.readAsDataURL(editedVideo);
    } catch (err) {
      alert(translations.exportVideo.videoProcessingError);
      console.error(err);
    } finally {
      setIsCompressing(false);
    }
  };

  return (
    <div className="boxform">
      <h1 className="title">{translations.exportVideo.title}</h1>

      {isCompressing && (
        <div className="compression-overlay">
          <div className="compression-modal">
            <h2>{translations.exportVideo.compressionInProgress}</h2>
            <div className="progress-bar-container">
              <div
                className="progress-bar-fill"
                style={{ width: `${compressionProgress}%` }}
              />
            </div>
            <p className="progress-text">{compressionProgress}%</p>
          </div>
        </div>
      )}

      {!showEditor && (
        <label className="file-upload-label">
          <input
            type="file"
            accept=".mp4, .m4v, .mov, .gif"
            onChange={handleVideoChange}
            className="file-upload-input"
            disabled={isCompressing}
          />
          <span className="file-upload-button">{translations.exportVideo.chooseButton}</span>
        </label>
      )}

      {showEditor && videoUrl && (
        <div className="video-editor-section" style={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}>

          {/* Prévisualisation vidéo avec zone de crop */}
          <div
            ref={containerRef}
            className="video-crop-container"
            onMouseDown={handleMouseDown}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            style={{
              position: 'relative',
              width: `${videoResolution.width * displayScale}px`,
              height: `${videoResolution.height * displayScale}px`,
              maxWidth: 'calc(100vw - 80px)',
              maxHeight: 'calc(100vh - 350px)',
              margin: '20px auto',
              border: '4px solid #000',
              boxShadow: '4px 4px 0px #000',
              backgroundColor: '#000',
              overflow: 'hidden',
              cursor: isDragging ? 'grabbing' : 'grab',
              touchAction: 'none'
            }}
          >
            <video
              ref={videoRef}
              src={videoUrl}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: `${videoResolution.width * displayScale}px`,
                height: `${videoResolution.height * displayScale}px`,
                display: 'block',
                pointerEvents: 'none',
                userSelect: 'none'
              }}
              onLoadedMetadata={(e) => {
                // Mettre à jour la position de la vidéo pour le trim
                if (videoRef.current) {
                  videoRef.current.currentTime = trimStart;
                }
              }}
            />

            {/* Overlay pour visualiser la zone de crop */}
            <svg
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none',
              }}
            >
              <defs>
                <mask id="cropMask">
                  <rect width="100%" height="100%" fill="white" />
                  <rect
                    x={`${(cropArea.x / videoResolution.width) * 100}%`}
                    y={`${(cropArea.y / videoResolution.height) * 100}%`}
                    width={`${(cropArea.width / videoResolution.width) * 100}%`}
                    height={`${(cropArea.height / videoResolution.height) * 100}%`}
                    fill="black"
                  />
                </mask>
              </defs>
              <rect
                width="100%"
                height="100%"
                fill="rgba(0, 0, 0, 0.6)"
                mask="url(#cropMask)"
              />
              {/* Cadre de crop */}
              <rect
                x={`${(cropArea.x / videoResolution.width) * 100}%`}
                y={`${(cropArea.y / videoResolution.height) * 100}%`}
                width={`${(cropArea.width / videoResolution.width) * 100}%`}
                height={`${(cropArea.height / videoResolution.height) * 100}%`}
                fill="none"
                stroke="#fff"
                strokeWidth="3"
                style={{ filter: 'drop-shadow(0 0 4px rgba(0,0,0,0.8))' }}
              />
            </svg>

            {/* Texte d'instruction */}
            <div style={{
              position: 'absolute',
              top: `${(cropArea.y / videoResolution.height) * 100 + (cropArea.height / videoResolution.height) * 50}%`,
              left: `${(cropArea.x / videoResolution.width) * 100 + (cropArea.width / videoResolution.width) * 50}%`,
              transform: 'translate(-50%, -50%)',
              color: '#fff',
              fontSize: '14px',
              fontWeight: 'bold',
              textShadow: '2px 2px 4px rgba(0, 0, 0, 0.8)',
              pointerEvents: 'none',
              opacity: isDragging ? 0.7 : 0.4,
            }}>
              {translations.exportVideo.moveFrame}
            </div>
          </div>

          <p className="image-instructions" style={{ marginTop: '10px', textAlign: 'center', width: '100%', maxWidth: 'min(640px, 90vw)' }}>
            {translations.exportVideo.cropInstructions}
          </p>

          {/* Timeline visuelle de trim */}
          <div className="timeline-section" style={{ margin: '20px auto', maxWidth: 'min(640px, 90vw)', width: '100%' }}>
            <h3 className="canvas-label-picker" style={{ textAlign: 'center' }}>
              {translations.exportVideo.timelineTitle} : {trimStart.toFixed(1)}s - {trimEnd.toFixed(1)}s
              <br />
              ({(trimEnd - trimStart).toFixed(1)}s / {MAX_DURATION_SECONDS}s {translations.exportVideo.timelineDuration})
            </h3>

            <div
              id="video-timeline"
              style={{
                position: 'relative',
                width: '100%',
                height: '60px',
                backgroundColor: '#e0e0e0',
                border: '3px solid #000',
                marginTop: '10px',
                cursor: 'pointer',
                userSelect: 'none'
              }}
            >
              {/* Zone non sélectionnée à gauche */}
              <div
                style={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  width: `${(trimStart / videoDuration) * 100}%`,
                  height: '100%',
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  pointerEvents: 'none'
                }}
              />

              {/* Zone sélectionnée */}
              <div
                onMouseDown={(e) => handleTimelineMouseDown(e, 'middle')}
                onTouchStart={(e) => handleTimelineTouchStart(e, 'middle')}
                style={{
                  position: 'absolute',
                  left: `${(trimStart / videoDuration) * 100}%`,
                  top: 0,
                  width: `${((trimEnd - trimStart) / videoDuration) * 100}%`,
                  height: '100%',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  cursor: 'grab',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontWeight: 'bold',
                  fontSize: '12px',
                  textShadow: '1px 1px 2px rgba(0,0,0,0.8)',
                  touchAction: 'none'
                }}
              >
                {(trimEnd - trimStart).toFixed(1)}s

                {/* Poignée de début */}
                <div
                  onMouseDown={(e) => handleTimelineMouseDown(e, 'start')}
                  onTouchStart={(e) => handleTimelineTouchStart(e, 'start')}
                  style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    width: '20px',
                    height: '100%',
                    backgroundColor: '#fff',
                    cursor: 'ew-resize',
                    borderRight: '2px solid #000',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    touchAction: 'none'
                  }}
                >
                  <div style={{
                    width: '3px',
                    height: '30px',
                    backgroundColor: '#000',
                    borderRadius: '1px'
                  }} />
                </div>

                {/* Poignée de fin */}
                <div
                  onMouseDown={(e) => handleTimelineMouseDown(e, 'end')}
                  onTouchStart={(e) => handleTimelineTouchStart(e, 'end')}
                  style={{
                    position: 'absolute',
                    right: 0,
                    top: 0,
                    width: '20px',
                    height: '100%',
                    backgroundColor: '#fff',
                    cursor: 'ew-resize',
                    borderLeft: '2px solid #000',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    touchAction: 'none'
                  }}
                >
                  <div style={{
                    width: '3px',
                    height: '30px',
                    backgroundColor: '#000',
                    borderRadius: '1px'
                  }} />
                </div>
              </div>

              {/* Zone non sélectionnée à droite */}
              <div
                style={{
                  position: 'absolute',
                  left: `${(trimEnd / videoDuration) * 100}%`,
                  top: 0,
                  width: `${((videoDuration - trimEnd) / videoDuration) * 100}%`,
                  height: '100%',
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  pointerEvents: 'none'
                }}
              />
            </div>

            <p className="image-instructions" style={{ marginTop: '10px', textAlign: 'center' }}>
              {translations.exportVideo.adjustInstructions}
            </p>
          </div>

          {/* Contrôles de zoom de crop */}
          <div className="crop-controls-section" style={{ margin: '20px auto', maxWidth: 'min(640px, 90vw)', width: '100%' }}>
            <h3 className="canvas-label-picker" style={{ textAlign: 'center' }}>{translations.exportVideo.zoomTitle}</h3>

            <div className="size-slider-group" style={{ marginTop: '15px', marginBottom: '15px' }}>
              <label className="canvas-label-picker">
                {translations.exportVideo.zoomLabel} <span className="size-value">{Math.round(cropScale * 100)}%</span>
              </label>
              <input
                type="range"
                min="0.1"
                max="3"
                step="0.01"
                value={cropScale}
                onChange={(e) => applyCropScale(parseFloat(e.target.value))}
                className="size-slider-input"
              />
            </div>

            <p className="image-instructions" style={{ textAlign: 'center' }}>
              {translations.exportVideo.currentArea} {Math.round(cropArea.width)}x{Math.round(cropArea.height)}px → 1920x1080px
            </p>
          </div>

          <div style={{
            textAlign: 'center',
            margin: '20px 0',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '10px'
          }}>
            <button onClick={handleConfirmEdit} className="submit-button">
              {translations.exportVideo.confirm}
            </button>
            <button
              onClick={() => {
                setShowEditor(false);
                setVideoUrl('');
                setOriginalFile(null);
              }}
              className="submit-button"
              style={{ background: 'linear-gradient(135deg, #ea6766 0%, #a24b76 100%)' }}
            >
              {translations.exportVideo.cancel}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default ExportVideo;
